import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Users as UsersIcon, AlertTriangle, X, Plus, Save } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

/**
 * Crew assignment picker with availability + soft conflict warnings.
 *
 * Props:
 *  - jobId: current job id (string)
 *  - selected: array of crew user ids currently assigned
 *  - start, end: scheduled_start/scheduled_end ISO strings (or null)
 *  - onSaved: callback after a successful save
 */
export default function CrewPicker({ jobId, selected, start, end, onSaved }) {
    const [open, setOpen] = useState(false);
    const [crew, setCrew] = useState([]);
    const [picked, setPicked] = useState(selected || []);
    const [conflicts, setConflicts] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => setPicked(selected || []), [selected]);

    // Load active crew once (so chips can be hydrated without opening the picker)
    useEffect(() => {
        api.get("/admin/users", { params: { role: "crew,contractor,supervisor" } })
            .then(({ data }) => setCrew(data.users.filter((u) => u.is_active)))
            .catch(() => {});
    }, []);

    // Whenever picked changes (and we have a time window), check conflicts.
    useEffect(() => {
        if (!open || !start || !end || picked.length === 0) {
            setConflicts([]);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get("/jobs/conflicts", {
                    params: {
                        start,
                        end,
                        crew: picked.join(","),
                        exclude_job_id: jobId,
                    },
                });
                setConflicts(data.conflicts || []);
            } catch (e) {
                /* ignore */
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [picked, start, end, jobId, open]);

    const conflictByCrew = useMemo(() => {
        const map = {};
        conflicts.forEach((c) => {
            c.crew_ids.forEach((cid) => {
                map[cid] = map[cid] || [];
                map[cid].push(c);
            });
        });
        return map;
    }, [conflicts]);

    const toggle = (id) => {
        setPicked((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            await api.put(`/jobs/${jobId}`, { crew: picked });
            // Close synchronously; defer the parent refetch a tick so this
            // render commits first.
            setOpen(false);
            setSaving(false);
            setTimeout(() => {
                if (onSaved) onSaved();
            }, 0);
        } catch (e) {
            setErr(apiErrorMessage(e));
            setSaving(false);
        }
    };

    const selectedNames = useMemo(() => {
        // Hydrate names from crew list when available; otherwise show ids.
        const m = Object.fromEntries(crew.map((c) => [c.id, c]));
        return picked.map((id) => m[id]).filter(Boolean);
    }, [picked, crew]);

    return (
        <div data-testid="crew-picker">
            <div className="flex items-center justify-between">
                <p className="eyebrow">Crew</p>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setOpen(true)}
                    data-testid="crew-picker-open"
                >
                    <Plus size={14} />
                    Edit
                </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="crew-picker-chips">
                {selected?.length === 0 || !selected ? (
                    <p className="text-sm text-muted">No crew assigned.</p>
                ) : selectedNames.length ? (
                    selectedNames.map((c) => (
                        <span key={c.id} className="badge-pill badge-crew">
                            {c.name}
                        </span>
                    ))
                ) : (
                    selected.map((id) => (
                        <span key={id} className="badge-pill badge-muted font-mono">
                            {id.slice(-6)}
                        </span>
                    ))
                )}
            </div>

            {open && createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
                    style={{ background: "rgba(26,26,26,0.5)" }}
                    onClick={() => setOpen(false)}
                    data-testid="crew-picker-modal"
                >
                    <div
                        className="brand-card w-full max-w-[520px] max-h-[80vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <p className="eyebrow">Assign</p>
                                <h3 className="h3 mt-2">Crew on this job</h3>
                                {start && end ? (
                                    <p className="text-xs text-muted mt-1">
                                        {fmtDateTime(start)} → {fmtDateTime(end)}
                                    </p>
                                ) : (
                                    <p className="text-xs text-muted mt-1">
                                        Set a schedule first to see conflicts.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {err && (
                            <div
                                className="field-error mt-3"
                                data-testid="crew-picker-error"
                            >
                                {err}
                            </div>
                        )}

                        <ul className="mt-4 flex flex-col gap-1">
                            {crew.length === 0 && (
                                <p className="text-muted text-sm">No active crew yet.</p>
                            )}
                            {crew.map((c) => {
                                const checked = picked.includes(c.id);
                                const conflictList = conflictByCrew[c.id] || [];
                                return (
                                    <li
                                        key={c.id}
                                        className="flex items-start gap-3 p-2 rounded-md"
                                        style={{
                                            background: checked
                                                ? "rgba(210,5,28,0.04)"
                                                : c.onboarding_status !== "complete"
                                                  ? "rgba(107,102,94,0.05)"
                                                  : "transparent",
                                            border: "1px solid var(--brand-surface)",
                                            opacity: c.onboarding_status !== "complete" ? 0.65 : 1,
                                        }}
                                        data-testid={`crew-picker-row-${c.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(c.id)}
                                            className="mt-1"
                                            disabled={c.onboarding_status !== "complete"}
                                            data-testid={`crew-picker-check-${c.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-display font-bold text-sm">
                                                {c.name || c.email}{" "}
                                                <span className="text-xs text-muted font-normal capitalize">
                                                    · {c.role}
                                                </span>
                                                {c.onboarding_status !== "complete" && (
                                                    <span
                                                        className="badge-pill badge-warning ml-2"
                                                        style={{ fontSize: "9px" }}
                                                        title="Cannot be assigned until onboarding is complete"
                                                    >
                                                        onboarding {String(c.onboarding_status || "").replace("_"," ")}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-muted truncate">
                                                {c.email}
                                            </p>
                                            {checked && conflictList.length > 0 && (
                                                <div
                                                    className="mt-2 flex items-start gap-2 text-xs"
                                                    style={{ color: "var(--brand-red)" }}
                                                    data-testid={`crew-picker-conflict-${c.id}`}
                                                >
                                                    <AlertTriangle size={12} className="mt-0.5" />
                                                    <span>
                                                        Conflict:{" "}
                                                        {conflictList.map((cf, i) => (
                                                            <span key={cf.job_id}>
                                                                {i > 0 ? ", " : ""}
                                                                {cf.job_number} (
                                                                {cf.client_name})
                                                            </span>
                                                        ))}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>

                        <button
                            type="button"
                            className="btn btn-primary w-full mt-5"
                            onClick={save}
                            disabled={saving}
                            data-testid="crew-picker-save"
                        >
                            <Save size={14} />
                            {saving ? "Saving…" : "Save assignment"}
                        </button>
                        {conflicts.length > 0 && (
                            <p
                                className="text-xs text-center mt-2"
                                style={{ color: "var(--brand-red)" }}
                            >
                                Conflicts will be saved anyway — soft warning only.
                            </p>
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
